import { useCallback, useEffect, useRef, useState } from 'react';
import { getEventsUrl } from '@/api/core/client';
import { parseEventStreamData } from '@/api/realtime';
import type { RealtimeEvent } from '@/types/email';

const BACKOFF_MS = [1000, 2000, 5000, 10000, 20000];
const MAX_RECONNECT_ATTEMPTS = 8;
const COOLDOWN_MS = 60_000;

type EventCallback = (event: RealtimeEvent) => void;

type Cleanup = () => void;

export type EventStreamStatus = 'idle' | 'connecting' | 'connected' | 'error';

interface UseEventStreamOptions {
  enabled?: boolean;
  authToken?: string | null;
  onStatusChange?: (status: EventStreamStatus) => void;
}

export function useEventStream(
  accountId: string | undefined,
  onEvent: EventCallback,
  options: UseEventStreamOptions = {}
): EventStreamStatus {
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;
  const { enabled = true, authToken = null, onStatusChange } = options;
  const [status, setStatus] = useState<EventStreamStatus>('idle');
  const statusRef = useRef<EventStreamStatus>('idle');
  const hiddenAtRef = useRef<number | null>(null);
  // Last SSE event id we've seen, so a (manual) reconnect can ask the server to
  // replay everything newer (audit §2.10 #6). Persists across reconnects within
  // this hook instance.
  const lastEventIdRef = useRef<string | null>(null);
  const [sseEnabled, setSseEnabled] = useState(true);

  // Only close SSE if hidden for extended period (5+ minutes) to prevent event buffer buildup
  // For brief tab switches, keep SSE open and process queued events normally
  const HIDDEN_THRESHOLD_MS = 5 * 60 * 1000;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const handleVisibility = () => {
      const isVisible = document.visibilityState === 'visible';
      if (!isVisible) {
        hiddenAtRef.current = Date.now();
      } else if (hiddenAtRef.current) {
        const hiddenDuration = Date.now() - hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (hiddenDuration > HIDDEN_THRESHOLD_MS) {
          // Extended absence - toggle SSE to force fresh connection (discards buffered events)
          setSseEnabled(false);
          // Re-enable on next tick to trigger reconnect
          setTimeout(() => setSseEnabled(true), 0);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const updateStatus = useCallback(
    (next: EventStreamStatus) => {
      if (statusRef.current === next) {
        return;
      }
      statusRef.current = next;
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      updateStatus('error');
      return undefined;
    }

    if (!enabled) {
      updateStatus('idle');
      return undefined;
    }

    if (!authToken) {
      updateStatus('idle');
      return undefined;
    }

    // Close SSE after extended absence to prevent browser from buffering too many events
    if (!sseEnabled) {
      updateStatus('idle');
      return undefined;
    }

    let cancelled = false;
    let reconnectAttempts = 0;
    let cleanup: Cleanup | null = null;
    let reconnectTimer: number | null = null;
    let cooldownTimer: number | null = null;

    const scheduleReconnect = () => {
      if (cancelled) {
        return;
      }
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        if (cooldownTimer !== null) {
          return;
        }
        console.warn('[EventStream] Max reconnect attempts reached. Cooling down before retrying…');
        cooldownTimer = window.setTimeout(() => {
          cooldownTimer = null;
          reconnectAttempts = 0;
          if (!cancelled) {
            if (cleanup) {
              cleanup();
              cleanup = null;
            }
            start();
          }
        }, COOLDOWN_MS);
        return;
      }
      const delay = BACKOFF_MS[Math.min(reconnectAttempts, BACKOFF_MS.length - 1)];
      reconnectAttempts += 1;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        if (!cancelled) {
          if (cleanup) {
            cleanup();
            cleanup = null;
          }
          start();
        }
      }, delay + Math.random() * 500);
    };

    const start = async () => {
      if (cancelled) {
        return;
      }
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
      updateStatus('connecting');
      let url: string;
      try {
        url = await getEventsUrl(accountId, lastEventIdRef.current);
      } catch (error) {
        console.warn('[EventStream] Could not get a stream ticket:', error);
        if (!cancelled) {
          updateStatus('error');
          scheduleReconnect();
        }
        return;
      }
      if (cancelled) {
        return;
      }
      const source = new EventSource(url, { withCredentials: true });

      source.onopen = () => {
        reconnectAttempts = 0;
        if (cooldownTimer !== null) {
          window.clearTimeout(cooldownTimer);
          cooldownTimer = null;
        }
        updateStatus('connected');
        console.log('[EventStream] Connected to SSE stream:', url);
      };

      source.onmessage = (event) => {
        // Track the last-seen event id for replay-on-reconnect. The browser
        // populates event.lastEventId from the SSE `id:` line.
        if (event.lastEventId) {
          lastEventIdRef.current = event.lastEventId;
        }

        // Skip empty messages
        if (!event.data || event.data.trim() === '') {
          return;
        }

        const parsed = parseEventStreamData(event.data);
        if (parsed) {
          callbackRef.current(parsed);
        }
        // Don't warn for null returns - parseEventStreamData handles status messages silently
      };

      source.onerror = () => {
        if (cancelled) {
          source.close();
          return;
        }
        updateStatus('error');
        source.close();
        scheduleReconnect();
      };

      cleanup = () => {
        source.close();
      };
    };

    start();

    return () => {
      cancelled = true;
      if (cleanup) {
        cleanup();
      }
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      if (cooldownTimer !== null) {
        window.clearTimeout(cooldownTimer);
      }
      cleanup = null;
      reconnectTimer = null;
      cooldownTimer = null;
      updateStatus('idle');
    };
  }, [accountId, enabled, authToken, sseEnabled, updateStatus]);

  return status;
}
